import { breakPoints } from '../../../config/saas-structure';
import { getFocusableElements } from '../utils/helpers';
import { promoTab } from './SP99_2-promo_tab';

type MainNavElements = {
    [property: string] : HTMLElement;
};

type MainNavElementCollections = {
    [property: string] : NodeListOf<HTMLElement>
};

type SiteHeaderInstance = {
    elements: MainNavElements;
    elementCollections: MainNavElementCollections;
    collapseDuration: number;
}

export const otMainNav = {
    reduceMotion: window.matchMedia(`(prefers-reduced-motion: reduce)`) && window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true,
    scrollBehavior: getComputedStyle(document.documentElement).scrollBehavior,
    lastScrollTop: 0,
    isNavJustClosed: false,
    resizing: false,
    DATA_ATTRS: {
        backBtn: 'data-ot-main-nav-back-btn',
        btnDropdownMenuMobile: 'data-ot-main-nav-btn-dropdown-menu-mobile',
        collapsible: 'data-ot-collapsible',
        collapsibleHidden: 'data-ot-collapsible-hidden',
        collapsibleNavItems: 'data-ot-collapsible-nav-items',
        collapseToggleBtn: 'data-ot-collapse-toggle-btn',
        collapseTransition: 'data-ot-collapse-transition',
        dropdownId: 'data-ot-main-nav-dropdown-id',
        dropdownIdMobile: 'data-ot-main-nav-dropdown-id-mobile',
        dropdownMenu: 'data-ot-main-nav-dropdown-menu',
        dropdownMenuMobile: 'data-ot-main-nav-dropdown-menu-mobile',
        mainNav: 'data-ot-main-nav',
        siteHeader: 'data-ot-site-header',
        swapContent: 'data-ot-swap-content',
    },
    async init() {
        const $siteHeaders = document.querySelectorAll(`[${this.DATA_ATTRS.siteHeader}]`) as NodeListOf<HTMLElement>;

        $siteHeaders.forEach($siteHeader => {
            const { elements, elementCollections } = this.getElements($siteHeader);

            let missingElement = false;

            Object.entries(elements).forEach(([key, $el]) => {
                if (!$el) {
                    console.warn(`${key} element not found in site header:`, $siteHeader);
                    missingElement = true;
                    return;
                }
            });

            if (missingElement) return;

            const collapseDurationStr = window.getComputedStyle(elements.$collapseTransition as HTMLElement).transitionDuration;
            const collapseDuration = collapseDurationStr.indexOf('ms') > 0 ? parseInt(collapseDurationStr) : (parseFloat(collapseDurationStr) * 1000) || 0;
            const instance = { elements, elementCollections, collapseDuration };

            promoTab.init();

            this.addEventListeners(instance);

            setTimeout(() => {
                document.querySelector('html')?.classList.add('scroll-smooth')
            }, 1000)
        });
    },
    getElements($siteHeader: HTMLElement): { elements: MainNavElements, elementCollections: MainNavElementCollections } {
        const $mainNav = $siteHeader.querySelector(`[${this.DATA_ATTRS.mainNav}]`) as HTMLElement;

        return {
            elements: {
                $mainNav,
                $mainNavScrollHide: $siteHeader.querySelector(`[${this.DATA_ATTRS.mainNav}]`) as HTMLElement,
                $collapsible: $mainNav?.querySelector(`[${this.DATA_ATTRS.collapsible}]`) as HTMLButtonElement,
                $collapsibleHidden: $mainNav?.querySelector(`[${this.DATA_ATTRS.collapsibleHidden}]`) as HTMLButtonElement,
                $collapsibleNavItems: $mainNav?.querySelector(`[${this.DATA_ATTRS.collapsibleNavItems}]`) as HTMLElement,
                $collapseToggleBtn: $mainNav?.querySelector(`[${this.DATA_ATTRS.collapseToggleBtn}]`) as HTMLButtonElement,
                $collapseTransition: $mainNav?.querySelector(`[${this.DATA_ATTRS.collapseTransition}]`) as HTMLElement,
            },
            elementCollections: {
                $dropdownBtns: $mainNav?.querySelectorAll(`[${this.DATA_ATTRS.dropdownId}]`) as NodeListOf<HTMLElement>,
            }
        }
    },
    isDesktopBreakpoint() {
        return matchMedia(`(min-width: ${breakPoints.lg.minWidth}px)`).matches;
    },
    updateCollapseToggleButton($toggleBtn: HTMLElement, action: 'open' | 'close'): void {
        const $toggleLine  = $toggleBtn?.querySelector('.navbar-toggle-line');

        if (!$toggleLine) return;

        const EXPANDED_CLASSES = ['bg-transparent', 'before:rotate-45', 'after:-rotate-45'];
        const COLLAPSED_CLASSES = ['bg-white', 'before:-translate-y-[.45em]', 'after:translate-y-[.45em]', 'after:scale-x-[85%]', 'after:origin-bottom-left', 'before:scale-x-[85%]', 'before:origin-top-left'];

        if (action === 'open') {
            $toggleLine.classList.add(...EXPANDED_CLASSES);
            $toggleLine.classList.remove(...COLLAPSED_CLASSES);
        } else {
            $toggleLine.classList.remove(...EXPANDED_CLASSES);
            $toggleLine.classList.add(...COLLAPSED_CLASSES);
        }

        const dataAttr = this.DATA_ATTRS.swapContent;
        const $toggleBtnText = $toggleBtn?.querySelector(`[${dataAttr}]`) as HTMLElement;

        if (!$toggleBtnText) return;

        const innerText = $toggleBtnText?.innerText;
        const swapContent = $toggleBtnText.getAttribute(dataAttr) || innerText;

        $toggleBtnText.innerHTML = swapContent;
        $toggleBtnText.setAttribute(dataAttr, innerText);
    },
    expand(instance: SiteHeaderInstance): void {
        const { elements, collapseDuration } = instance;
        const { $collapsible, $collapsibleHidden, $collapseTransition, $collapseToggleBtn }  = elements;

        if ($collapsible.getAttribute(this.DATA_ATTRS.collapsible) === 'expanded') return;

        // Update attributes
        $collapsibleHidden.removeAttribute('hidden');
        $collapsible.setAttribute(this.DATA_ATTRS.collapsible, 'expanding');

        // Update classes
        $collapseTransition.classList.remove($collapseTransition.dataset.removeClass || '' as string);
        $collapseTransition.classList.add($collapseTransition.dataset.addClass || '' as string);

        this.updateCollapseToggleButton($collapseToggleBtn, 'open');

        setTimeout(() => {
            // After transition complete
            $collapsible.setAttribute(this.DATA_ATTRS.collapsible, 'expanded');
        }, collapseDuration);
    },
    collapse(instance: SiteHeaderInstance): void {
        const { elements, collapseDuration } = instance;
        const { $collapsible, $collapsibleHidden, $collapseTransition, $collapseToggleBtn }  = elements;

        if ($collapsible.getAttribute(this.DATA_ATTRS.collapsible) === 'collapsed') return;

        // Update attributes
        $collapsible.setAttribute(this.DATA_ATTRS.collapsible, 'collapsing');

        // Update classes
        $collapseTransition.classList.remove($collapseTransition.dataset.addClass || '' as string);
        $collapseTransition.classList.add($collapseTransition.dataset.removeClass || '' as string);

        this.updateCollapseToggleButton($collapseToggleBtn, 'close');

        setTimeout(() => {
            // After transition complete
            $collapsible.setAttribute(this.DATA_ATTRS.collapsible, 'collapsed');
            $collapsibleHidden.setAttribute('hidden', '');
            instance.elements.$collapsibleNavItems.hidden = false;
            this.dropdowns.closeAll(instance);
        }, collapseDuration);
    },
    isExpanded($collapsible: HTMLElement) {
        return $collapsible.getAttribute(this.DATA_ATTRS.collapsible) === 'expanded';
    },
    dropdowns: {
        setPosition($mainNav: HTMLElement, $dropdownMenu: HTMLElement): void {
            // Ensure right side of menu never passes right side of main nav element
            const $dropdownList = $dropdownMenu?.getElementsByTagName('ul')[0];
            const $container = $mainNav.querySelector<HTMLElement>('.container');

            $dropdownMenu.style.marginLeft = '0'; // Reset it so it can calculate again

            if (!$dropdownList) return;

            const ulBoundsRight = $dropdownList.getBoundingClientRect().right || 0;
            const containerBoundsRight = $container?.getBoundingClientRect().right || 0;
            const containerPaddingRight = $container && parseInt(window.getComputedStyle($container).paddingRight) || 0;
            const rightBoundary = containerBoundsRight - containerPaddingRight;

            if (ulBoundsRight > rightBoundary) {
                const offset = rightBoundary - ulBoundsRight;
                $dropdownMenu.style.marginLeft = offset + 'px';
            }
        },
        open(instance: SiteHeaderInstance, dropdownElements: MainNavElements): void {
            const { $mainNav } = instance.elements;
            const { $dropdownBtn, $dropdownMenu } = dropdownElements;

            if (!$dropdownMenu) {
                console.warn('dropdowns.open: $dropdownMenu is missing.');
                return;
            }

            const $arrowIcon = $dropdownBtn?.querySelector('.arrow-icon') as HTMLElement;
            $arrowIcon?.classList.add('rotate-180');
            $dropdownMenu.hidden = false;

            if (otMainNav.isDesktopBreakpoint()) {
                this.setPosition($mainNav, $dropdownMenu);
            } else {
                if (!$dropdownMenu.hasAttribute(otMainNav.DATA_ATTRS.dropdownMenuMobile)) {
                    // Button dropdowns only
                    instance.elements.$collapsibleNavItems.hidden = true;

                    document.addEventListener('keydown', (e) => {
                        // Move focus to first item in dropdown
                        if (e.key === 'Tab') {
                            e.preventDefault();
                            $dropdownMenu.querySelector('a')?.focus();
                        }
                    }, { once: true });
                }

                otMainNav.expand(instance);
            }
        },
        close(dropdownElements: MainNavElements): void {
            const { $dropdownBtn, $dropdownMenu} = dropdownElements;

            if (!$dropdownMenu) {
                console.warn('dropdowns.close: $dropdownMenu is missing.');
                return;
            }

            const $arrowIcon = $dropdownBtn?.querySelector('.arrow-icon') as HTMLElement;
            $arrowIcon?.classList.remove('rotate-180');
            $dropdownMenu.hidden = true;
        },
        closeClickedDropdown(instance: SiteHeaderInstance, dropdownElements: MainNavElements): void {
            if (!dropdownElements.$dropdownMenu) {
                console.warn('dropdowns.closeClickedDropdown: $dropdownMenu is missing.');
                return;
            }

            if (!dropdownElements.$dropdownMenu.hasAttribute(otMainNav.DATA_ATTRS.dropdownMenuMobile)) {
                if (otMainNav.isDesktopBreakpoint()) {
                    this.close(dropdownElements);
                }

                // Close immediately (since collapse() calls closeDropdown() after transition, resulting in a delay)
                otMainNav.collapse(instance);
            } else if (!otMainNav.isDesktopBreakpoint()) {
                this.close(dropdownElements);
            }
        },
        closeAll(instance: SiteHeaderInstance, $clickedDropdownBtn?: HTMLElement): void {
            const { $mainNav } = instance.elements;
            const $dropdownBtns = $mainNav?.querySelectorAll(`[${otMainNav.DATA_ATTRS.dropdownId}], [${otMainNav.DATA_ATTRS.dropdownIdMobile}]`) as NodeListOf<HTMLElement>;

            if (!$dropdownBtns) {
                console.warn('dropdowns.closeAll: No dropdown buttons found.');
                return;
            }

            $dropdownBtns.forEach($dropdownBtn => {
                if ($dropdownBtn === $clickedDropdownBtn) return;

                const id = $dropdownBtn.getAttribute(otMainNav.DATA_ATTRS.dropdownId);
                const idMobile = $dropdownBtn.getAttribute(otMainNav.DATA_ATTRS.dropdownIdMobile);
                const $dropdownMenu = $mainNav?.querySelector(`#${id ? id : idMobile}`) as HTMLElement;

                if (!$dropdownMenu) {
                    console.warn('dropdowns.closeAll: $dropdownMenu not found for button:', $dropdownBtn);
                    return;
                }

                this.close({ $dropdownBtn, $dropdownMenu })
            })
        },
        handleBackBtnClick(instance: SiteHeaderInstance): void {
            instance.elements.$collapsibleNavItems.hidden = false;
            otMainNav.dropdowns.closeAll(instance);

            document.addEventListener('keydown', (e) => {
                // Move focus to first item in dropdown
                if (e.key === 'Tab') {
                    e.preventDefault();
                    const $focusable = getFocusableElements(instance.elements.$collapsibleNavItems);
                    $focusable.all[0].focus();
                }
            }, { once: true });
        },
        handleBtnClick(instance: SiteHeaderInstance, $dropdownBtn: HTMLElement, dropdownMenuId: string): void {
            const $dropdownMenu = document.getElementById(dropdownMenuId);

            if (!$dropdownMenu) {
                console.warn('dropdowns.handleBtnClick: $dropdownMenu not found for id:', dropdownMenuId);
                return;
            }

            if ($dropdownMenu.hidden) {
                this.closeAll(instance, $dropdownBtn)
                this.open(instance, {$dropdownBtn, $dropdownMenu})
            } else {
                this.closeClickedDropdown(instance, {$dropdownBtn, $dropdownMenu})
            }
        },
    },
    addEventListeners(instance: SiteHeaderInstance): void {
        const { elements } = instance;

        function clickListener(): void {
            window.addEventListener('click', (e): void => {
                const $target = e.target as HTMLElement;

                if (!($target instanceof HTMLElement)) return;

                const dropdownMenuId = (otMainNav.isDesktopBreakpoint() ? 
                    $target.getAttribute(otMainNav.DATA_ATTRS.dropdownId) : 
                    $target.getAttribute(otMainNav.DATA_ATTRS.dropdownIdMobile)
                );

                if (elements.$collapseToggleBtn?.contains($target)) {
                    // Toggle Button clicked
                    otMainNav.isExpanded(elements.$collapsible) ? otMainNav.collapse(instance) : otMainNav.expand(instance);
                } else if (dropdownMenuId) {
                    // Dropdown Button clicked
                    otMainNav.dropdowns.handleBtnClick(instance, $target, dropdownMenuId);
                } else if ($target.hasAttribute(otMainNav.DATA_ATTRS.backBtn)) {
                    // Back Button clicked
                    otMainNav.dropdowns.handleBackBtnClick(instance);
                } else if ($target.dataset.otModalShow) {
                    // Modal trigger clicked
                    otMainNav.collapse(instance); // Always collapse nav when any modal toggle is clicked
                } else if (!$target.closest(`[${otMainNav.DATA_ATTRS.siteHeader}]`) || $target.hasAttribute('href')) {
                    // Anywhere outside Site Header clicked
                    otMainNav.collapse(instance); // Always collapse when clicking outside of main nav
                    otMainNav.dropdowns.closeAll(instance);
                } else if ($target.closest(`[${otMainNav.DATA_ATTRS.siteHeader}]`) && otMainNav.isDesktopBreakpoint()) {
                    // Anywhere inside Site Header click while at lg viewport size
                    otMainNav.dropdowns.closeAll(instance);
                }
            });
        }

        function keyListeners(): void {
            elements.$mainNav?.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    otMainNav.collapse(instance);
                    elements.$collapseToggleBtn?.focus();
                } else if (e.key === 'Tab' && !e.shiftKey) {
                    const $focusableLast = getFocusableElements(elements.$collapsibleNavItems)?.last;

                    if ($focusableLast && $focusableLast === e.target) {
                        otMainNav.collapse(instance);
                    }
                }
            });

            const $btnDropdownMobileMenus = 
                elements.$mainNav?.querySelectorAll(`[${otMainNav.DATA_ATTRS.btnDropdownMenuMobile}`) as NodeListOf<HTMLElement>;

            // Focus trap for mobile btn dropdown menus
            $btnDropdownMobileMenus?.forEach($dropdownMenu => {
                $dropdownMenu?.addEventListener('keydown', (e) => {
                    if (e.key !== 'Tab') return;

                    const $focusable = getFocusableElements($dropdownMenu);

                    if (!e.shiftKey && document.activeElement === $focusable?.last) {
                        e.preventDefault();
                        $focusable?.first?.focus();
                    }

                    if (e.shiftKey && document.activeElement === $focusable?.first) {
                        e.preventDefault();
                        $focusable?.last?.focus();
                    }
                });
            });
        }

        function resizeListener(): void {
            let resizeDebounce: number | undefined | ReturnType<typeof setTimeout> = 0;

            window.addEventListener('resize', (): void => {
                clearTimeout(resizeDebounce);
                otMainNav.resizing = true;

                resizeDebounce = setTimeout(() => {
                    if (otMainNav.isDesktopBreakpoint()) {
                        // Close collapsible when resized to >= lg
                        otMainNav.collapse(instance);
                    } else {
                        // Close dropdowns when resized to < lg
                        const lgDropdownMenus = elements.$mainNav.querySelectorAll(`[${otMainNav.DATA_ATTRS.dropdownMenu}]`);

                        lgDropdownMenus.forEach(menu => {
                            const $dropdownMenu = menu as HTMLElement;
                            const $dropdownBtn = elements.$mainNav.querySelector(`#${$dropdownMenu.id}-btn`) as HTMLElement;

                            if ($dropdownBtn) {
                                otMainNav.dropdowns.close({ $dropdownBtn, $dropdownMenu })
                            }
                        });
                    }

                    otMainNav.resizing = false;
                }, 100);
            });
        }

        function scrollListener(): void {
            if (!elements.$mainNavScrollHide) return;

            const handleScroll = (): void => {
                const $collapsible = elements.$collapsible;

                if (otMainNav.resizing || $collapsible.getAttribute(otMainNav.DATA_ATTRS.collapsible) !== 'collapsed') return;

                const THRESHOLD = 50;
                const TRANSITION_CLASSES = ['transition-transform', 'duration-300', 'ease-in-out'];
                const HIDDEN_CLASSES = ['translate-y-[calc(-100%-2px)]'];
                const SHOWN_CLASSES = ['translate-y-0']

                const $mainNavScrollHide = elements.$mainNavScrollHide;
                const scrollTop = window.scrollY || document.documentElement.scrollTop;

                $mainNavScrollHide.classList.add(...TRANSITION_CLASSES);

                if (scrollTop > otMainNav.lastScrollTop && scrollTop > THRESHOLD) {
                    // Scrolling down
                    $mainNavScrollHide.classList.add(...HIDDEN_CLASSES);
                    $mainNavScrollHide.classList.remove(...SHOWN_CLASSES);
                } else {
                    // Scrolling up
                    $mainNavScrollHide.classList.remove(...HIDDEN_CLASSES);
                    $mainNavScrollHide.classList.add(...SHOWN_CLASSES);
                }

                otMainNav.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
            };

            const LEADING_DELAY = 300;
            const MIN_INTERVAL = 500;

            let throttleTimeout: ReturnType<typeof setTimeout> | null = null;
            let lastExecution = 0;

            const scrollHandler = (): void => {
                if (throttleTimeout || otMainNav.resizing) return;

                const now = Date.now();
                const timeSinceLast = now - lastExecution;

                // Delay first run by 50ms or maintain minimum 200ms interval
                const delay = timeSinceLast > MIN_INTERVAL ? LEADING_DELAY : MIN_INTERVAL - timeSinceLast;

                throttleTimeout = setTimeout(() => {
                    handleScroll();
                    lastExecution = Date.now();
                    throttleTimeout = null;
                }, delay);
            };

            window.addEventListener('scroll', scrollHandler);
        }

        clickListener();
        keyListeners();
        resizeListener();

        setTimeout(() => {
            scrollListener();
        }, 250);
    },
}
